import { getBackendUrl } from "@Utils";
import { useTranslation, untranslated } from "@i18n";
import React, { useState, useEffect } from "react";
import axiosWrapper from "@AxiosWrapper";
import { useDispatch, useSelector } from "react-redux";
import FormPreview from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/formPreview";
import { formCategories } from "@Helpers";
import RecursiveConfirmationPopup from "../RecursiveConfirmationPopup";
import { saveCustomFieldsData } from "@Actions/fileUpload";
import Text from "@CubeComponents/html/Text/Text";
import type { SetFileFormTemplateRequest, StandardResponse } from "@Types/fileOperations";
import { getTemplates } from "@Actions/Forms";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import _ from "lodash";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { RootState } from "@Reducers/index";

interface CustomFieldsContentProps {
  hierarchyId: string;
  resourceType: string;
  selectedFormTemplateData: any;
  setUpdatedFormTemplateId: (data: any) => void;
  onChange?: (data: Record<string, any>) => void;
  customFieldsKey?: string;
  accessPermission: any;
}

function CustomFieldsContent({
  hierarchyId,
  resourceType,
  onChange,
  selectedFormTemplateData,
  setUpdatedFormTemplateId,
  accessPermission,
  customFieldsKey = "customFields",
}: Readonly<CustomFieldsContentProps>) {
  const { t } = useTranslation();
  const [formTemplate, setFormTemplate] = useState<any>({ template: [] });
  const [changedFormTemplateId, setChangedFormTemplateId] = useState<string | null | undefined>(null);

  const [showConfirmationPopup, setShowConfirmationPopup] = useState<boolean>(false);
  const [isRecursiveValue, setIsRecursiveValue] = useState<boolean>(false);
  const [formTemplateOptions, setFormTemplateOptions] = useState<any[]>([]);

  const { layout, projectData } = useSelector((state: RootState) => state);

  // Fetch form template options

  const dispatch = useDispatch();
  const fetchFormTemplateOptions = async () => {
    try {
      const response = await getTemplates({
        isArchived: false,
        value: {
          resourceType: formCategories.files,
          isArchived: false,
        },
      });
      if ((response.data as any)?.result) {
        setFormTemplateOptions((response.data as any).result);
      }
    } catch (e) {
      console.error("Error fetching form template options:", e);
      Promise.reject(e);
    }
  };

  useEffect(() => {
    fetchFormTemplateOptions();
  }, []);

  useEffect(() => {
    if (selectedFormTemplateData.formTemplate) {
      setFormTemplate({ template: selectedFormTemplateData.formTemplate });
    }
  }, [selectedFormTemplateData.formTemplate]);

  const handleSaveCustomFields = async (fieldsData: any[]) => {
    if (fieldsData.length) {
      saveCustomFieldsData({
        id: hierarchyId,
        formTemplateId: selectedFormTemplateData.formTemplateId,
        fieldsData,
      });
      if (onChange) {
        const key = `${customFieldsKey}.`;
        onChange({
          [`${key}${fieldsData[0]._id}`]: fieldsData[0].value,
        });
      }
    }
  };

  const handleTemplateChange = async (newValue: any) => {
    setShowConfirmationPopup(true);
    setChangedFormTemplateId(newValue?._id);
  };

  const handleSubmit = async () => {
    // `projectData.id` can be undefined, so `projectId` is widened by indexing into the request
    // type rather than re-declared as `string | undefined` — re-declaring would drop whatever
    // type the request field carries (and its brand, once `setFileFormTemplateSchema` is tightened).
    const payload: Omit<SetFileFormTemplateRequest, "projectId"> & {
      projectId: SetFileFormTemplateRequest["projectId"] | undefined;
    } = {
      id: hierarchyId,
      formTemplateId: changedFormTemplateId!,
      projectId: projectData.id,
      isRecursive: isRecursiveValue,
    };
    const response = (await axiosWrapper.post<StandardResponse<unknown>>(
      `${getBackendUrl()}/fileOperations/setFileFormTemplate`,
      payload,
    )) as {
      data: StandardResponse<unknown>;
    };
    if (response.data.status) {
      setUpdatedFormTemplateId(changedFormTemplateId!);
      dispatch(
        showToastNotification({
          content: t("Template changed successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(response.data.errorMessage as string),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    setShowConfirmationPopup(false);
  };

  const handleClose = () => {
    setShowConfirmationPopup(false);
    setIsRecursiveValue(false);
  };

  const toggleIsRecursive = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsRecursiveValue(event.target.value === "overRide");
  };

  const content = () => (
    <div className="flex flex-col">
      {layout.FILES?.ENABLE && resourceType == "RESOURCE_TYPE_FOLDER" && (
        <div className="flex mb-4 items-center">
          <div className="text-sm font-semibold flex-2 px-3">
            <Text>{t("Files")}</Text>
          </div>
          <div style={{ flex: "2", fontSize: "0.875rem" }}>
            <div style={{ width: "fit-content", minWidth: "12.5rem" }}>
              {/* <AutoComplete
                  options={formTemplateOptions}
                  valueField="_id"
                  getOptionLabel={(option: any) => option?.name || ""}
                  value={formTemplateOptions.find((opt: any) => opt._id === selectedFormTemplateId) || null}
                  onChange={handleTemplateChange}
                  renderInput={(params: any) => <TextFieldComponent {...params} />}
                /> */}
              <SelectDropDown
                onChange={handleTemplateChange}
                value={selectedFormTemplateData.formTemplateId}
                buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                fields={{ text: "name", value: "_id" }}
                options={formTemplateOptions}
                buttonWidth="100%"
                popupAlign="right"
                disabled={!accessPermission.write}
                popperWidth={400}
              />
            </div>
          </div>
        </div>
      )}
      {[
        "RESOURCE_TYPE_FILE",
        "RESOURCE_TYPE_MERGED_FILE",
        "RESOURCE_TYPE_SHORTCUT_FILE",
        "RESOURCE_TYPE_SHORTCUT_MERGED_FILE",
      ].includes(resourceType) && (
        <FormPreview
          formData={formTemplate}
          showInSidebar
          disabled={!accessPermission.write}
          allowBorder={false}
          setFormData={setFormTemplate}
          handleSaveCustomFields={handleSaveCustomFields}
          headingFontSize="text-base"
        />
      )}
    </div>
  );

  return (
    <>
      {content()}
      {showConfirmationPopup && (
        <RecursiveConfirmationPopup
          header="form template"
          handleSubmit={handleSubmit}
          handleClose={handleClose}
          toggleIsRecursive={toggleIsRecursive}
          module="folder"
          isRecursive={!!isRecursiveValue}
          optionButtonsText={[]}
          headerTitle={""}
          recursiveOverrideWarningMessage={t(
            "This action will override all existing data in custom fields in all existing Files in this Folder. This can not be undone.",
          )}
        />
      )}
    </>
  );
}

export default CustomFieldsContent;
